import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
  baseURL: `${BASE}/api`,
  withCredentials: true,
});

// Grouped, searchable catalog of event types.
export const eventTypeGroups = [
  {
    label: "Weddings & Milestones",
    items: [
      "Wedding", "Elopement", "Micro Wedding", "Destination Wedding", "Vow Renewal",
      "Rehearsal Dinner", "Bridal Shower", "Bachelor Party", "Bachelorette Party",
      "Engagement Party", "Wedding Expo", "Anniversary Party",
    ],
  },
  {
    label: "Private Celebrations",
    items: [
      "Birthday Party", "Sweet 16", "Quinceañera", "Bar / Bat Mitzvah",
      "Graduation Party", "Retirement Party", "Housewarming Party",
      "Gender Reveal", "Baby Shower", "Sip & See", "Family Reunion",
      "Holiday Party", "Memorial Gathering", "Celebration of Life",
    ],
  },
  {
    label: "Children's Events",
    items: [
      "Kids Birthday Party", "School Dance", "Field Day", "Carnival",
      "Character Meet & Greet", "Princess Party", "Superhero Party",
      "Summer Camp", "Easter Egg Hunt", "Halloween Event",
      "Christmas Party", "Youth Group Event",
    ],
  },
  {
    label: "Corporate",
    items: [
      "Company Party", "Corporate Holiday Party", "Award Banquet", "Conference",
      "Trade Show", "Product Launch", "Networking Event", "Grand Opening",
      "Ribbon Cutting", "Employee Appreciation", "Team Building",
      "Training Seminar", "Customer Appreciation", "Fundraising Gala",
    ],
  },
  {
    label: "Community",
    items: [
      "Community Festival", "Block Party", "County Fair", "Farmers Market",
      "Outdoor Concert", "Movie Night", "Food Truck Rally", "Cultural Festival",
      "Holiday Festival", "Charity Walk", "Parade", "Car Show", "Community Picnic",
    ],
  },
  {
    label: "Sports & Recreation",
    items: [
      "3-on-3 Basketball", "Dodgeball Tournament", "Volleyball Tournament",
      "Pickleball Tournament", "Cornhole Tournament", "Softball Tournament",
      "Soccer Tournament", "Flag Football Tournament", "Disc Golf Tournament",
      "Golf Tournament", "Running Race", "Fun Run", "Obstacle Course Race",
      "Strongman Competition", "Powerlifting Meet", "CrossFit Competition",
      "Cheer Competition", "Dance Competition", "Martial Arts Tournament",
      "Esports Tournament",
    ],
  },
  {
    label: "Entertainment",
    items: [
      "Karaoke Night", "Trivia Night", "Bingo Night", "Comedy Show",
      "Live Music", "Open Mic", "Talent Show", "Battle of the Bands",
      "DJ Dance Party", "Silent Disco", "Murder Mystery Dinner",
      "Casino Night", "Game Night", "Watch Party",
    ],
  },
  {
    label: "Faith-Based",
    items: [
      "Church Conference", "Worship Night", "Youth Conference",
      "Men's Retreat", "Women's Retreat", "Marriage Conference",
      "Vacation Bible School", "Church Picnic", "Mission Fundraiser",
      "Prayer Breakfast",
    ],
  },
  {
    label: "Fundraising",
    items: [
      "Charity Gala", "Silent Auction", "Live Auction", "Golf Fundraiser",
      "Benefit Concert", "5K Fundraiser", "Charity Dinner",
      "Pancake Breakfast", "Community Auction", "Giving Campaign",
    ],
  },
  {
    label: "Educational",
    items: [
      "Workshop", "Class", "Career Fair", "College Fair", "STEM Event",
      "Science Fair", "Book Fair", "Speaker Series",
      "Leadership Conference", "Business Seminar",
    ],
  },
  {
    label: "Outdoor & Adventure",
    items: [
      "Campout", "Guided Hike", "Fishing Tournament", "Hunting Competition",
      "Jeep Meetup", "Overlanding Event", "Motorcycle Rally",
      "Bicycle Ride", "Kayak Event", "Camping Festival",
    ],
  },
  {
    label: "Hobby & Interest",
    items: [
      "Comic Con", "Board Game Convention", "Card Tournament",
      "Craft Fair", "Maker Faire", "Home & Garden Show",
      "Bridal Show", "Pet Expo", "Reptile Expo", "Toy Show",
      "Gun Show", "Collectible Show",
    ],
  },
  {
    label: "Food & Beverage",
    items: [
      "Wine Tasting", "Beer Festival", "Whiskey Tasting", "Food Festival",
      "BBQ Competition", "Chili Cook-Off", "Rib Festival",
      "Dessert Festival", "Cooking Competition", "Chef Demonstration",
    ],
  },
  {
    label: "Nightlife",
    items: [
      "Club Night", "Theme Party", "Costume Party", "Glow Party",
      "New Year's Eve Party", "Singles Mixer", "Speed Dating",
      "Latin Dance Night", "Country Dance Night",
    ],
  },
  {
    label: "Seasonal",
    items: [
      "Christmas Festival", "Easter Celebration", "Fourth of July",
      "Halloween Festival", "Thanksgiving Dinner", "Valentine's Event",
      "Mother's Day Event", "Father's Day Event",
      "Fall Festival", "Winter Festival",
    ],
  },
];

// Convert to flat list of {value, label} where value is a slug and label is the display name.
function _slug(s) { return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""); }

export const eventTypes = eventTypeGroups.flatMap(g =>
  g.items.map(name => ({ value: _slug(name), label: name, group: g.label }))
);

// Reverse lookup helper.
export const eventTypeLabel = (value) => {
  const t = eventTypes.find(e => e.value === value);
  return t ? t.label : value;
};

export const vendorCategories = [
  "Catering", "Florist", "Photographer", "Videographer", "DJ / Band",
  "Venue", "Officiant", "Hair & Makeup", "Cake", "Transportation",
  "Rentals", "Lighting", "Stationery", "AV / Tech", "Signage",
  "Security", "Bartending", "Decor / Design", "Entertainment",
  "Auctioneer", "Referees", "Medical / EMT", "Sanitation / Restrooms",
  "Planner", "Other",
];

export const budgetCategories = [
  "Venue", "Catering", "Photography", "Videography", "Florals",
  "Music / Entertainment", "AV / Tech", "Rentals", "Decor", "Signage",
  "Stationery", "Transportation", "Attire", "Hair & Makeup",
  "Prizes / Awards", "Permits", "Insurance", "Marketing", "Miscellaneous",
];

export function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const now = new Date();
  const ms = d.setHours(0,0,0,0) - new Date(now.setHours(0,0,0,0)).valueOf();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

export function money(n) {
  const v = Number(n || 0);
  return v.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}
